import React from 'react';
import { useHistory } from 'react-router-dom';
import { CustomDropdownItem, Render, useToggle } from '@oforce/global-components';
import { useParseCoverageParameters } from '../../../views/coverageParameters/hooks';
import { HideFromRoles, HideFromTypes, useActiveAccount } from '../../../shared/activeAccount';
import { ActionDropdown, LabelValueCell, CellTable, Td, Tr } from '../../../components/table-2.0';
import { backendNameToLabel, formatCurrency } from '../../../utils/formatting';
import { DuplicatePricingOption, PricingRemove } from '.';
import { HideFromRolesAndTypes } from '../../../shared/activeAccount/components';
import { useCanadianProvinces, useUSStates } from '../../../hooks';
import { formatPricing, pricingLabel } from '../../exposureElections/utils';

export default function PricingRow({ policy, pricing }) {
  const history = useHistory();
  const {
    ROLES: { EXPOSURE_MANAGER, OBSERVER },
    TYPES: { CLIENT, CLAIMS_ADJUSTER }
  } = useActiveAccount();

  const [showDuplicate, toggleDuplicate] = useToggle();
  const [showRemove, toggleRemove] = useToggle();
  const insuranceTypeRequiresVehicle = policy?.product?.insuranceType?.requireVehicle;

  return (
    <>
      <Tr>
        <Td>
          <NameCell name={pricing?.name} label={pricing?.label} />
        </Td>
        <Td>
          <PricingCell
            factor={pricing?.factoredField}
            rate={pricing?.rate}
            mode={pricing?.rateMode}
            isPercentageFactor={pricing?.isPercentageFactor}
          />
        </Td>
        <Td>
          <MinMaxCell mode={pricing?.rateMode} min={pricing?.percentageMinimum} max={pricing?.percentageMaximum} />
        </Td>
        <HideFromTypes types={[CLIENT]}>
          <HideFromRoles roles={[EXPOSURE_MANAGER]}>
            <Td>
              <CommissionCell commission={pricing?.retailCommission} />
            </Td>
          </HideFromRoles>
        </HideFromTypes>
        <Td>
          <FeesCell fees={pricing?.fees} />
        </Td>
        <Render if={insuranceTypeRequiresVehicle}>
          <Td>
            <VehicleTypesCell vehicleTypes={pricing?.vehicleTypes} />
          </Td>
        </Render>
        <Td>
          <CoverageParameterCell coverageParameters={pricing?.coverageParameters} />
        </Td>
        <Td align="right">
          <HideFromTypes types={[CLIENT]}>
            <HideFromRolesAndTypes types={[CLAIMS_ADJUSTER]} roles={[EXPOSURE_MANAGER, OBSERVER]}>
              <ActionDropdown
                onEdit={() => history.push(`/policies/${policy?.id}/pricing/${pricing?.id}/update`)}
                editText="Edit"
                onRemove={toggleRemove}
              >
                <CustomDropdownItem color="info" onClick={toggleDuplicate} icon={['far', 'copy']}>
                  Duplicate
                </CustomDropdownItem>
              </ActionDropdown>
            </HideFromRolesAndTypes>
          </HideFromTypes>
        </Td>
      </Tr>

      <PricingRemove policy={policy} pricing={pricing} show={showRemove} toggle={toggleRemove} />
      <DuplicatePricingOption policy={policy} pricing={pricing} show={showDuplicate} toggle={toggleDuplicate} />
    </>
  );
}

const NameCell = ({ name, label }) => <LabelValueCell label={label} value={name} />;

const VehicleTypesCell = ({ vehicleTypes }) => {
  if (vehicleTypes.length === 0) return <EmptyCell />;

  return vehicleTypes.reduce((acc, type, i) => {
    if (i === 0) return `${type.name}`;
    return `${acc}, ${type.name}`;
  }, '');
};

const CommissionCell = ({ commission }) => {
  if (!commission) return <EmptyCell />;

  const formattedRate = formatPricing({
    rate: commission?.rate,
    rateMode: commission?.rateMode,
    isPercentageFactor: true
  });
  return <LabelValueCell label={formattedRate} value={commission?.name} />;
};

const FeesCell = ({ fees }) => {
  if (!fees) return <EmptyCell />;

  const feeRows = fees.reduce((acc, { rate, rateMode, name }) => {
    if (rate)
      return [
        ...acc,
        { label: name, value: formatPricing({ rate, rateMode, isPercentageFactor: rateMode === 'PERCENTAGE' }) }
      ];
    return acc;
  }, []);

  return feeRows.length > 0 ? <CellTable rows={feeRows} /> : <EmptyCell />;
};

const CoverageParameterCell = ({ coverageParameters }) => {
  const { options: stateOptions } = useUSStates();
  const { options: provinceOptions } = useCanadianProvinces();
  const allStateOptions = [...stateOptions, ...provinceOptions];
  const { prepareParameterValueForUi } = useParseCoverageParameters();

  const paramRows = coverageParameters.reduce((acc, param) => {
    const { name: label, value, type, valueType } = param;
    if (value && type === 'LIST' && valueType === 'STATE') {
      const parsedValues = JSON.parse(value);
      const labelIncludeExclude = parsedValues?.length <= allStateOptions?.length / 2 ? 'Applies to' : 'Excluded from';
      const values =
        parsedValues?.length <= allStateOptions?.length / 2
          ? parsedValues
          : allStateOptions
              ?.filter(({ value }) => !parsedValues?.map(value => value)?.includes(value))
              ?.map(({ value }) => value);

      return [
        ...acc,
        {
          label,
          value: values?.join(', '),
          numState: values?.length,
          labelIncludeExclude: labelIncludeExclude,
          stateList: true,
          id: Math.random().toString().split('.')[1]
        }
      ];
    } else if (value) {
      return [...acc, { label, value: prepareParameterValueForUi(param) }];
    }
    return acc;
  }, []);

  return paramRows.length > 0 ? <CellTable rows={paramRows} /> : <EmptyCell />;
};

const PricingCell = ({ rate, mode, factor, isPercentageFactor }) => {
  const value = pricingLabel({ factoredField: factor }) || backendNameToLabel(mode);

  return <LabelValueCell label={formatPricing({ rate, mode, isPercentageFactor })} value={value} />;
};

const MinMaxCell = ({ mode, min, max }) => {
  if (mode === 'PERCENTAGE' || mode === 'FACTORED') {
    const minLabel = min && formatCurrency(min);
    const maxLabel = max && formatCurrency(max);

    if (min && max) {
      return <LabelValueCell label={`${minLabel} - ${maxLabel}`} />;
    } else if (min && !max) {
      return <LabelValueCell label={`> ${minLabel}`} />;
    } else if (!min && max) {
      return <LabelValueCell label={` < ${maxLabel}`} />;
    } else {
      return <EmptyCell />;
    }
  }
  return <EmptyCell text="Inactive" />;
};

const EmptyCell = ({ text = 'None' }) => <div className="font-weight-300 fs-xs font-italic text-muted">{text}</div>;
